/*MinhaLoja /index */

import React, { useState, useEffect, useRef } from 'react';
import {  
  LayoutDashboard, Users, Settings, Menu, X, Store, LogOut, ExternalLink,  
  Package, Layers, ListPlus, Bike, Bell, Ticket, Image as ImageIcon,  
  Megaphone, Trophy, ChevronDown, ChevronUp, MessageSquare, MessageCircle, QrCode,
  Cpu, Globe, AlertTriangle, CreditCard, ArrowUpCircle
} from 'lucide-react';
import api from '../../services/api';
import toast, { Toaster } from 'react-hot-toast';
import io from 'socket.io-client';

// Componentes
import Pedidos from './Pedidos';
import Equipe from './Equipe';
import Configuracoes from './Configuracoes';
import Produtos from './Produtos';
import Categorias from './Categorias';
import Adicionais from './Adicionais'; 
import Entregas from './Entregas'; 
import Dashboard from '../../components/Dashboard';

// Novos Componentes de Faturas e Planos
import Faturas from './Faturas';
import PlanosUpgrade from './PlanosUpgrade';

// Marketing
import Cupons from './Marketing/Cupons';
import Fidelidade from './Marketing/Fidelidade';
import BannersManager from './Marketing/BannersManager';
import AlertasManager from './Marketing/AlertasManager';
// WhatsApp & WebChat
import WhatsAppConfig from './WhatsApp/WhatsAppConfig'; 
import WhatsAppChat from './WhatsApp/WhatsAppChat';
import TemplateManager from './WhatsApp/TemplateManager';
import BotFlowEditor from './WhatsApp/BotFlowEditor'; 
import WebChatConfig from './WhatsApp/WebChatConfig';

import '../../styles/MinhaLoja.css';

const MinhaLoja = () => {
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('pedidos');
  const [loading, setLoading] = useState(true);
  const [lojaInfo, setLojaInfo] = useState({
    id: localStorage.getItem('estabelecimentoId'),
    nome: '', logo_url: '', slug: '', cargo: ''
  });

  // Estados do Modal de Alerta Obrigatório
  const [alertaModalOpen, setAlertaModalOpen] = useState(false);
  const [mensagemAlerta, setMensagemAlerta] = useState('');

  const [openMenus, setOpenMenus] = useState({
    atendimento: true,
    cardapio: true,
    marketing: false,
    gestao: true,
    whatsapp: true 
  });

  const [novosPedidosCount, setNovosPedidosCount] = useState(0);
  const [mensagensNaoLidasCount, setMensagensNaoLidasCount] = useState(0);

  const ultimoIdRef = useRef(null);
  
  // Inicializa com som padrão, mas será substituído caso exista no BD
  const audioRef = useRef(new Audio('https://assets.mixkit.co/active_storage/sfx/2257/2257-preview.mp3'));

  const BASE_URL = 'https://app.universomidiadigital.com.br';

  // Desbloqueio de áudio para navegadores
  const desbloquearAudio = () => {
    const audio = audioRef.current;
    audio.play()
      .then(() => {
        audio.pause();
        audio.currentTime = 0;
        document.removeEventListener('click', desbloquearAudio);
      })
      .catch(e => console.log("Aguardando interação para liberar áudio..."));
  };

  useEffect(() => {
    document.addEventListener('click', desbloquearAudio);
    return () => document.removeEventListener('click', desbloquearAudio);
  }, []);

  // Função para checar mensagens não lidas via API
  const verificarMensagensNaoLidas = async (id) => {
    try {
      const res = await api.get(`/whatsapp/chats/${id}`);
      const chats = res.data?.payload || res.data || [];
      if (Array.isArray(chats)) {
        const totalNaoLidas = chats.reduce((acc, c) => {
          const unread = Number(c.unreadCount) || Number(c.unread_count) || 0;
          return acc + unread;
        }, 0);
        if (totalNaoLidas > 0) {
          setMensagensNaoLidasCount(totalNaoLidas);
        }
      }
    } catch (err) {
      console.error("Erro ao verificar mensagens não lidas", err);
    }
  };

  // Carrega informações da loja e verifica dados iniciais
  useEffect(() => {
    const storedId = localStorage.getItem('estabelecimentoId');
    if (storedId) {
      api.get(`/cardapio/estabelecimento/${storedId}`)
        .then(res => {
          const cargoUsuario = localStorage.getItem('cargoUsuario') || res.data.cargo || 'gerente';
          
          // Busca o som configurado no banco de dados e atualiza o audioRef
          const somUrl = res.data?.audio_notificacao || res.data?.payload?.audio_notificacao || 'https://assets.mixkit.co/active_storage/sfx/2257/2257-preview.mp3';
          audioRef.current = new Audio(somUrl);
          
          setLojaInfo({ ...res.data, cargo: cargoUsuario });
          setLoading(false);
          verificarNovosPedidos(storedId, true);
          verificarMensagensNaoLidas(storedId);

          if (res.data.alerta_ativo === 1 && res.data.alerta_popup) {
            setMensagemAlerta(res.data.alerta_popup);
            setAlertaModalOpen(true);
          }
        })
        .catch(() => setLoading(false));
    }

    const interval = setInterval(() => {
      const currentId = localStorage.getItem('estabelecimentoId');
      if (currentId && activeTab !== 'whatsapp-chat') {
        verificarNovosPedidos(currentId, false);
        verificarMensagensNaoLidas(currentId);
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [activeTab]);

  // Conexão Socket.io unificada para tempo real
  useEffect(() => {
    const storedId = localStorage.getItem('estabelecimentoId');
    if (!storedId) return;

    const socket = io(BASE_URL, {
      transports: ['websocket', 'polling']
    });

    socket.on('connect', () => {
      socket.emit('join', storedId);
      socket.emit('entrar_sala_estabelecimento', storedId);
    });

    socket.on('receber_alerta_admin', (data) => {
      setMensagemAlerta(data.mensagem);
      setAlertaModalOpen(true);
    });

    socket.on('admin-alert', (data) => {
      setMensagemAlerta(data.mensagem);
      setAlertaModalOpen(true);
    });

    // Ouve a nova mensagem em tempo real
    socket.on('whatsapp-new-message', (data) => {
      const isFromMe = data.fromMe === true || data.fromMe === 1 || data.from_me === 1;

      if (!isFromMe) {
        // Toca o som de notificação se o chat não estiver aberto
        const som = audioRef.current;
        if (som && activeTab !== 'whatsapp-chat') {
          som.currentTime = 0;
          som.play().catch(e => console.warn("Som bloqueado", e));
        }

        // Incrementa o badge na hora se o usuário não estiver na aba do chat
        if (activeTab !== 'whatsapp-chat') {
          setMensagensNaoLidasCount(prev => prev + 1);
        }
      }
    });

    return () => socket.disconnect();
  }, [activeTab]);

  const handleMarcarComoVisto = async () => {
    try {
      if (!lojaInfo.id) return;
      await api.post(`/cardapio/estabelecimentos/${lojaInfo.id}/limpar-alerta`);
      setAlertaModalOpen(false);
      setMensagemAlerta('');
      toast.success("Alerta marcado como visto!");
    } catch (err) {
      toast.error("Erro ao atualizar o status do alerta.");
    }
  };

  const toggleAccordion = (menu) => {
    if (!isSidebarOpen) setSidebarOpen(true);
    setOpenMenus(prev => ({ ...prev, [menu]: !prev[menu] }));
  };

  const verificarNovosPedidos = async (id, isFirstLoad) => {
    try {
      const res = await api.get(`/pedidos/loja/${id}?pedido_status=pendente`);
      const pedidosPendentes = res.data;

      if (pedidosPendentes.length > 0) {
        const maiorId = Math.max(...pedidosPendentes.map(p => p.id));

        if (!isFirstLoad && ultimoIdRef.current && maiorId > ultimoIdRef.current) {
          const som = audioRef.current;
          som.currentTime = 0;
          som.play().catch(e => console.warn("Som bloqueado", e));

          toast.error(`🚀 NOVO PEDIDO RECEBIDO!`, {
            duration: 5000,
            icon: '🔔'
          });
        }
        
        ultimoIdRef.current = maiorId;
        setNovosPedidosCount(pedidosPendentes.length);
      } else {
        setNovosPedidosCount(0);
      }
    } catch (err) { 
      console.error("Erro na vigia de pedidos"); 
    }
  };

  // Renderiza item de navegação com badge de alta ênfase (destaque total)
  const renderNavItem = (id, icon, label, badgeCount = 0) => (
    <button 
      className={`nav-item ${activeTab === id ? 'active' : ''}`} 
      onClick={() => {
        setActiveTab(id);
        if (id === 'whatsapp-chat') {
          setMensagensNaoLidasCount(0); // Zera o contador ao abrir o chat
        }
      }}
      style={{ position: 'relative' }}
    >
      <div className="nav-icon-container" style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {icon}
        {badgeCount > 0 && (
          <span style={{
            position: 'absolute',
            top: '-8px',
            right: '-12px',
            backgroundColor: '#ef4444',
            color: '#ffffff',
            borderRadius: '9999px',
            padding: '2px 7px',
            fontSize: '11px',
            fontWeight: '800',
            lineHeight: '1.2',
            boxShadow: '0 0 0 2px #ffffff, 0 4px 6px -1px rgba(239, 68, 68, 0.5)',
            zIndex: 10,
            animation: 'pulse 1.5s infinite ease-in-out'
          }}>
            {badgeCount}
          </span>
        )}
      </div>
      {isSidebarOpen && <span>{label}</span>}
    </button>
  );

  const logoUrlFinal = lojaInfo.logo_url?.startsWith('http') 
    ? lojaInfo.logo_url 
    : `${BASE_URL}${lojaInfo.logo_url}`;

  const cargoAtual = String(lojaInfo.cargo || '').toLowerCase();
  const eGerenteOuSupervisor = cargoAtual.includes('gerente') || cargoAtual.includes('supervisor');

  return (
    <div className="minha-loja-layout">
      <Toaster position="top-right" />
        
      {alertaModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(15, 23, 42, 0.75)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
          padding: '20px'
        }}>
          <div style={{
            background: '#ffffff',
            width: '100%',
            maxWidth: '480px',
            borderRadius: '16px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden',
            border: '1px solid #e2e8f0',
            animation: 'fadeInModal 0.25s ease-out'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              padding: '24px',
              color: '#ffffff',
              textAlign: 'center',
              position: 'relative'
            }}>
              <div style={{
                width: '56px',
                height: '56px',
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px auto'
              }}>
                <AlertTriangle size={32} color="#ffffff" />
              </div>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>Aviso Importante</h2>
              <p style={{ margin: '4px 0 0 0', fontSize: '13px', opacity: 0.9 }}>Administração Universo Mídia Digital</p>
            </div>

            <div style={{ padding: '24px', color: '#334155', fontSize: '15px', lineHeight: '1.6', textAlign: 'center' }}>
              <p style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {mensagemAlerta}
              </p>
            </div>

            <div style={{ padding: '16px 24px 24px 24px', background: '#f8fafc', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'center' }}>
              <button 
                onClick={handleMarcarComoVisto}
                style={{
                  background: '#2563eb',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  width: '100%',
                  transition: 'background 0.2s',
                  boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)'
                }}
              >
                Entendi e Marcar como Visto
              </button>
            </div>
          </div>
        </div>
      )}

      <aside className={`sidebar ${isSidebarOpen ? 'expanded' : 'collapsed'}`}>
        <div className="sidebar-header">
          {isSidebarOpen && (
            <div className="logo-area">
              {lojaInfo.logo_url && <img src={logoUrlFinal} alt="Logo" className="sidebar-logo" />}
              <span className="logo-text">PAINEL<strong>GESTOR</strong></span>
            </div>
          )}
          <button className="toggle-sidebar" onClick={() => setSidebarOpen(!isSidebarOpen)}>
            {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <nav className="sidebar-nav custom-scrollbar">
          {/* ATENDIMENTO */}
          <div className="nav-group">
            <div className="nav-group-header" onClick={() => toggleAccordion('atendimento')}>
              {isSidebarOpen && <span>ATENDIMENTO</span>}
              {isSidebarOpen && (openMenus.atendimento ? <ChevronUp size={14}/> : <ChevronDown size={14}/>)}
            </div>
            {(openMenus.atendimento || !isSidebarOpen) && (
              <div className="nav-group-content">
                {renderNavItem('pedidos', 
                  <Bell size={22} className={novosPedidosCount > 0 ? 'animate-bounce' : ''} 
                        style={{ color: novosPedidosCount > 0 ? '#ef4444' : 'inherit' }} />, 
                  "Pedidos Online",
                  novosPedidosCount
                )}
                {renderNavItem('dashboard', <LayoutDashboard size={22} />, "Estatísticas", 0)}
              </div>
            )}
          </div>

          {/* WHATSAPP & WEBCHAT */}
          <div className="nav-group">
            <div className="nav-group-header" onClick={() => toggleAccordion('whatsapp')}>
              {isSidebarOpen && <span>WHATSAPP</span>}
              {isSidebarOpen && (openMenus.whatsapp ? <ChevronUp size={14}/> : <ChevronDown size={14}/>)}
            </div>
            {(openMenus.whatsapp || !isSidebarOpen) && (
              <div className="nav-group-content">
                {renderNavItem(
                  'whatsapp-chat', 
                  <MessageCircle size={22} color="#25D366" />, 
                  "Chat em Tempo Real",
                  mensagensNaoLidasCount
                )}
                {renderNavItem('whatsapp-bot', <Cpu size={22} color="#8b5cf6" />,"Robô[Em Desenvolvimento..]", 0)}
                {renderNavItem('webchat-config', <Globe size={22} color="#00a884" />, "Configurar Webchat", 0)}
                {renderNavItem('whatsapp-templates', <ListPlus size={22} color="#34b7f1" />, "Templates de Bot", 0)}
                {renderNavItem('whatsapp-config', <QrCode size={22} />, "Conectar Aparelho", 0)}
              </div>
            )}
          </div>

          {/* CARDÁPIO */}
          <div className="nav-group">
            <div className="nav-group-header" onClick={() => toggleAccordion('cardapio')}>
              {isSidebarOpen && <span>CARDÁPIO</span>}
              {isSidebarOpen && (openMenus.cardapio ? <ChevronUp size={14}/> : <ChevronDown size={14}/>)}
            </div>
            {(openMenus.cardapio || !isSidebarOpen) && (
              <div className="nav-group-content">
                {renderNavItem('categorias', <Layers size={22} />, "Categorias", 0)}
                {renderNavItem('produtos', <Package size={22} />, "Produtos", 0)}
                {renderNavItem('adicionais', <ListPlus size={22} />, "Adicionais", 0)}
              </div>
            )}
          </div>
            
          {/* MARKETING */}
          <div className="nav-group">
            <div className="nav-group-header" onClick={() => toggleAccordion('marketing')}>
              {isSidebarOpen && <span>MARKETING</span>}
              {isSidebarOpen && (openMenus.marketing ? <ChevronUp size={14}/> : <ChevronDown size={14}/>)}
            </div>
            {(openMenus.marketing || !isSidebarOpen) && (
              <div className="nav-group-content">
                {renderNavItem('cupons', <Ticket size={22} />, "Cupons", 0)}
                {renderNavItem('fidelidade', <Trophy size={22} />, "Fidelidade", 0)}
                {renderNavItem('banners', <ImageIcon size={22} />, "Banners", 0)}
                {renderNavItem('alertas', <Megaphone size={22} />, "Alertas", 0)}
              </div>
            )}
          </div>

          {/* GESTÃO */}
          <div className="nav-group">
            <div className="nav-group-header" onClick={() => toggleAccordion('gestao')}>
              {isSidebarOpen && <span>GESTÃO</span>}
              {isSidebarOpen && (openMenus.gestao ? <ChevronUp size={14}/> : <ChevronDown size={14}/>)}
            </div>
            {(openMenus.gestao || !isSidebarOpen) && (
              <div className="nav-group-content">
                {renderNavItem('entregas', <Bike size={22} />, "Taxas de Entrega", 0)}
                {renderNavItem('equipe', <Users size={22} />, "Equipe Digital", 0)}
                {renderNavItem('config', <Settings size={22} />, "Configurações", 0)}

                {eGerenteOuSupervisor && (
                  <>
                    {renderNavItem('faturas', <CreditCard size={22} color="#3b82f6" />, "Faturas e Pagamentos", 0)}
                    {renderNavItem('planos-upgrade', <ArrowUpCircle size={22} color="#10b981" />, "Upgrade de Planos", 0)}
                  </>
                )}
              </div>
            )}
          </div>
        </nav>

        <div className="sidebar-footer">
          <button className="nav-item logout" onClick={() => { localStorage.clear(); window.location.href = '/'; }}>
            <LogOut size={22} />
            {isSidebarOpen && <span>Sair</span>}
          </button>
        </div>
      </aside>

      <main className="main-wrapper">
        <header className="main-header">
          <div className="header-left">
            <span className="store-breadcrumb">
                {lojaInfo.nome || 'Painel'} / 
                <span className="breadcrumb-active">
                    {` ${activeTab.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}`}
                </span>
            </span>
          </div>
          <div className="header-right">
             <button className="btn-view-store" onClick={() => window.open(`/${lojaInfo.slug}`, '_blank')}>
                <ExternalLink size={16} /> Ver Cardápio Público
             </button>
          </div>
        </header>

        <div className="page-content custom-scrollbar">
          {loading ? (
            <div className="loader-container"><div className="spinner"></div></div>
          ) : (
            <div className="content-inner animate-fade-in">
              {activeTab === 'pedidos' && <Pedidos estabelecimentoId={lojaInfo.id} />}
              {activeTab === 'dashboard' && <Dashboard estabelecimentoId={lojaInfo.id} />}
              {activeTab === 'categorias' && <Categorias estabelecimentoId={lojaInfo.id} />}
              {activeTab === 'produtos' && <Produtos estabelecimentoId={lojaInfo.id} />}
              {activeTab === 'adicionais' && <Adicionais estabelecimentoId={lojaInfo.id} />}
              {activeTab === 'entregas' && <Entregas estabelecimentoId={lojaInfo.id} />}
              {activeTab === 'equipe' && <Equipe estabelecimentoId={lojaInfo.id} />}
              {activeTab === 'config' && <Configuracoes dadosIniciais={lojaInfo} onUpdate={(d) => setLojaInfo(p => ({...p, ...d}))} />}
              
              {eGerenteOuSupervisor && activeTab === 'faturas' && <Faturas estabelecimentoId={lojaInfo.id} />}
              {eGerenteOuSupervisor && activeTab === 'planos-upgrade' && <PlanosUpgrade estabelecimentoId={lojaInfo.id} />}

              {activeTab === 'cupons' && <Cupons estabelecimentoId={lojaInfo.id} />}
              {activeTab === 'fidelidade' && <Fidelidade estabelecimentoId={lojaInfo.id} />}
              
              {activeTab === 'banners' && <BannersManager estabelecimentoId={lojaInfo.id} />}
              {activeTab === 'alertas' && <AlertasManager estabelecimentoId={lojaInfo.id} />}
              
              {activeTab === 'whatsapp-chat' && <WhatsAppChat estabelecimentoId={lojaInfo.id} />}
              {activeTab === 'whatsapp-bot' && <BotFlowEditor estabelecimentoId={lojaInfo.id} />}
              {activeTab === 'webchat-config' && <WebChatConfig estabelecimentoId={lojaInfo.id} />}
              {activeTab === 'whatsapp-templates' && <TemplateManager estabelecimentoId={lojaInfo.id} />}
              {activeTab === 'whatsapp-config' && <WhatsAppConfig estabelecimentoId={lojaInfo.id} />}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default MinhaLoja;